<template>
  <div class="cockpit-ppc-chart" data-testid="cockpit-ppc-chart">
    <div v-if="isEmpty" class="text-sm text-[var(--color-disabled)] py-8 text-center h-48 flex items-center justify-center">
      {{ $t('UserProjects.cockpit.chart.empty') }}
    </div>
    <div v-else class="cockpit-ppc-chart__canvas">
      <Line :data="chartData" :options="chartOptions" />
    </div>
    <div class="flex items-center gap-4 mt-3 text-[12px] text-[#6B6B6B]">
      <span class="flex items-center gap-1.5">
        <span class="w-2.5 h-2.5 rounded-full bg-[#e4821a]" />
        {{ $t('UserProjects.cockpit.chart.legendPpc') }}
      </span>
      <span class="flex items-center gap-1.5">
        <span class="w-4 h-0.5 bg-[#22C55E]" />
        {{ $t('UserProjects.cockpit.chart.legendTarget') }}
      </span>
    </div>
  </div>
</template>

<script>
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const bandPlugin = {
  id: 'cockpitPpcBands',
  beforeDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const yScale = scales.y;
    if (!yScale || !chartArea) return;

    const bands = [
      { from: 80, to: 100, color: 'rgba(34, 197, 94, 0.06)' },
      { from: 60, to: 80, color: 'rgba(234, 179, 8, 0.06)' },
      { from: 0, to: 60, color: 'rgba(239, 68, 68, 0.06)' },
    ];

    bands.forEach(({ from, to, color }) => {
      const y1 = yScale.getPixelForValue(to);
      const y2 = yScale.getPixelForValue(from);
      ctx.save();
      ctx.fillStyle = color;
      ctx.fillRect(chartArea.left, y1, chartArea.right - chartArea.left, y2 - y1);
      ctx.restore();
    });
  },
  afterDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const yScale = scales.y;
    if (!yScale || !chartArea) return;
    const y = yScale.getPixelForValue(80);
    ctx.save();
    ctx.strokeStyle = '#22C55E';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(chartArea.left, y);
    ctx.lineTo(chartArea.right, y);
    ctx.stroke();
    ctx.restore();
  },
};

ChartJS.register(bandPlugin);

export default {
  name: 'CockpitPpcTrendChart',
  components: { Line },
  props: {
    bars: { type: Array, default: () => [] },
  },
  computed: {
    isEmpty() {
      return !this.bars.some((bar) => typeof bar.ppc === 'number');
    },
    chartData() {
      return {
        labels: this.bars.map((bar) => bar.label),
        datasets: [
          {
            label: 'PPC',
            data: this.bars.map((bar) => bar.ppc),
            borderColor: '#e4821a',
            backgroundColor: '#e4821a',
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.25,
            spanGaps: true,
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => (ctx.parsed.y == null ? '—' : `${ctx.parsed.y}%`),
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: '#ABABAB', font: { size: 11 } },
          },
          y: {
            min: 0,
            max: 100,
            ticks: {
              stepSize: 20,
              color: '#ABABAB',
              font: { size: 11 },
              callback: (value) => `${value}%`,
            },
            grid: { color: '#E5E5E5' },
          },
        },
      };
    },
  },
};
</script>

<style scoped>
.cockpit-ppc-chart {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid #e5e5e5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.cockpit-ppc-chart__canvas {
  height: 220px;
}
</style>
